import { builderDispatch, PrismUI } from '../../_utils/ui';
import isCard from '../utils/is-card';
import { usePrismItemStore } from './usePrismItemStore';


export class ThemeStore {
  constructor() {
    this.themes = [];
    this.products = [];
    this.fetching = false;
  }

  async fetch() {
    this.themes = [];
    this.fetching = true;

    const [_, themes, products] = await Promise.all([
      this.dispatchThemes(),
      this.getThemes(),
      this.getProducts(),
    ]);

    this.products = products;
    this.themes = this.filterThemes(themes, products);
    this.fetching = false;

    this.dispatchThemes();

    return this.themes;
  }

  async length() {
    // Fetch the themes if they don't exist
    const themes = this.themes.length > 0
      ? this.themes
      : await this.fetch();

    return themes.length;
  }

  async some() {
    return (await this.length()) > 0;
  }

  filterThemes(themes, products) {
    // Return all if it's not a card
    if (!isCard()) return this.orderByBestSeller(themes);

    const filters = this.getCurrentProductFilters(products);

    const filtered = themes.filter((theme) =>
      // return true if one or more of the filters matches
      // the current product
      this
        .getProductFiltersByDesignCode(products, theme.design_code)
        .some((f) => filters.includes(f)));

    // If there are no matches then return them all
    return filtered.length > 0
      ? this.orderByBestSeller(filtered)
      : themes;
  }

  orderByBestSeller(items) {
    return items.sort((a, b) => ((a.sales_total < b.sales_total) ? 1 : -1));
  }

  async getThemes() {
    const response = await fetch(wkData.themes_url, {
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(response.statusText);

    const { themes } = await response.json();
    return themes || [];
  }

  async getProducts() {
    const url = new URL(wkData.info_url);
    const path = url.pathname;
    const response = await fetch(`${path}/themes.json`, {
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(response.statusText);

    const { products } = await response.json();
    return products || [];
  }

  async dispatchThemes() {
    return builderDispatch.delayedDispatch({
      type: PrismUI.THEMES_LOADED,
      themes: this.themes,
    });
  }

  getProductFiltersByDesignCode(products, code) {
    const product = products.find((p) => p.design_code === code);
    const filters = product ? product.filters : {};
    // Return the top-level key plus a compound "key:value" token for each
    // sub-value title, enabling two levels of specificity:
    // e.g. { seasonal: [{title:"easter"}] } → ["seasonal", "seasonal:easter"]
    return Object.entries(filters).flatMap(([key, values]) => {
      const subTokens = (Array.isArray(values) ? values : [])
        .map((v) => v.title ? `${key}:${v.title.toLowerCase()}` : null)
        .filter(Boolean);
      return subTokens.length > 0 ? [key, ...subTokens] : [key];
    });
  }

  getCurrentProductFilters(products) {
    // Get a list of filters for the current prism item
    // but exclude any that we're not interested in
    const prismItem = usePrismItemStore.getState().prismItem;

    return this
      .getProductFiltersByDesignCode(products, prismItem.design_code)
      .filter((filter) => this.cardFilters.some((cf) => filter === cf || filter.startsWith(`${cf}:`)));
  }

  get cardFilters() {
    return [
      'baby',
      'birthday',
      'business',
      'christmas_and_holiday',
      'espanol_card',
      'everyday',
      'graduation',
      'licensed_character',
      'religiou',
      'seasonal',
      'specialty_card',
      'thank_you_card',
      'wedding',
    ];
  }

  /**
   * Returns occasion-relevant filter tokens (top-level keys + compound
   * "key:value" tokens) for the currently active design. Only includes tokens
   * whose key is in the cardFilters allowlist so that non-occasion attributes
   * like "size", "paper", "colour" do not pollute the match.
   */
  getOccasionFilters() {
    if (this.products.length === 0) return [];
    const prismItem = usePrismItemStore.getState().prismItem;
    return this.getProductFiltersByDesignCode(this.products, prismItem.design_code)
      .filter((f) => this.cardFilters.some((cf) => f === cf || f.startsWith(`${cf}:`)));
  }

  /**
   * Returns filter tokens for any design_code using the cached products data.
   */
  getFiltersByDesignCode(designCode) {
    return this.getProductFiltersByDesignCode(this.products, designCode);
  }
}

export const themeStore = new ThemeStore();
